// SalesManList.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "../../App";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";

import { getAllBranchLists } from "../../service/branchService";
import {
  getAllSalesManList,
  deleteSalesMan,
} from "../../service/salesmanService";
import SalesMan from "./SalesMan";

// Custom debounce hook (works perfectly)
const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value.trim());
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const SalesManList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* ────────────────────── STATE ────────────────────── */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [searchBranch, setSearchBranch] = useState("All");
  const [branches, setBranches] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", branch_id: "", mobile: "", id: undefined });

  const { showSnackbar } = useSnackbar();
  const menuOpen = Boolean(anchorEl);

  /* ────────────────────── LOAD BRANCHES ────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const r = await getAllBranchLists();
        setBranches(Array.isArray(r?.data?.data) ? r.data.data : []);
      } catch {
        showSnackbar("Failed to load branches", "error");
      }
    })();
  }, [showSnackbar]);

  /* ────────────────────── FETCH SALESMEN (FIXED & FINAL) ────────────────────── */
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(paginationModel.page + 1),           // Laravel: 1-based
        per_page: String(paginationModel.pageSize),      // Correct: per_page
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }), // Correct: search=
        ...(searchBranch !== "All" && { branch_id: searchBranch }),
      });

      const res = await getAllSalesManList(`?${params.toString()}`);

      // Correct path from your actual API response
      const apiData = res?.data?.data;
      const list = apiData?.data || [];
      const total = apiData?.total || 0;

      const formatted = list.map((i) => ({
        id: i.id,
        name: i.name,
        branch: i.branch?.name || "—",
        mobile: i.mobile || "—",
        branch_id: i.branch_id,
      }));

      setRows(formatted);
      setRowCount(total);
    } catch (e) {
      showSnackbar(e?.response?.data?.message || "Failed to load salesmen", "error");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearchTerm,
    searchBranch,
    showSnackbar,
  ]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Reset page when search or branch filter changes
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [debouncedSearchTerm, searchBranch]);

  /* ────────────────────── MENU HANDLERS ────────────────────── */
  const openMenu = (e, row) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const startEdit = (row) => {
    setFormData({
      id: row.id,
      name: row.name,
      branch_id: row.branch_id || "",
      mobile: row.mobile || "",
    });
    setOpenModal(true);
    closeMenu();
  };

  const startCreate = () => {
    setFormData({ name: "", branch_id: "", mobile: "", id: undefined });
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setFormData({ name: "", branch_id: "", mobile: "", id: undefined });
  };

  const afterSubmit = () => {
    closeModal();
    fetch();
  };

  const handleDelete = () => {
    if (!selectedRow?.id) {
      showSnackbar("No salesman selected for deletion!", "error");
      return;
    }
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedRow?.id) return;

    try {
      setLoading(true);
      await deleteSalesMan(selectedRow.id);
      showSnackbar("Salesman deleted successfully!", "success");
      await fetch();
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to delete salesman!", "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setSelectedRow(null);
      setAnchorEl(null);
    }
  };

  /* ────────────────────── DATAGRID COLUMNS ────────────────────── */
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: isMobile ? 80 : 100,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <IconButton onClick={(e) => openMenu(e, p.row)} size="small">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "branch", headerName: "Branch", flex: 1, minWidth: 150 },
    { field: "mobile", headerName: "Mobile", flex: 1, minWidth: 140 },
  ];

  /* ────────────────────── RENDER (UI UNCHANGED) ────────────────────── */
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* FILTER BAR */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "stretch" : { sm: "flex-start", md: "flex-start", lg: "space-between" }}
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mb={2}
      >
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 1 : "10px"}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          <TextField
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: isMobile ? "100%" : { sm: 200, md: 220, lg: 280 } }}
          />

          <FormControl size="small" sx={{ width: isMobile ? "100%" : { sm: 200, md: 180, lg: 200 } }}>
            <Select
              value={searchBranch}
              onChange={(e) => {
                setSearchBranch(e.target.value);
              }}
            >
              <MenuItem value="All">All Branches</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSearchTerm("");
              setSearchBranch("All");
            }}
            sx={{
              borderColor: Color.secondary.main,
              color: Color.secondary.main,
              width: isMobile ? "100%" : { sm: 70, md: 80, lg: 90 },
              height: isMobile ? "34px" : "37.69px",
            }}
          >
            Clear
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={startCreate}
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : { sm: 160, md: 170, lg: 200 },
            whiteSpace: "nowrap",
            fontSize: isMobile ? "0.85rem" : "1rem",
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
        >
          Add SalesMan
        </Button>
      </Box>

      {/* DATAGRID */}
      <Box sx={{ width: "100%", mt: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          rowHeight={isMobile ? 48 : 56}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          disableColumnMenu
              disableColumnSorting
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 600,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
            "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f4f4f4" },
            "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
            "& .MuiLinearProgress-colorPrimary": { backgroundColor: Color.secondary.main },
            overflowX: isMobile ? "hidden" : "auto",
          }}
        />
      </Box>

      {/* ACTION MENU */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        PaperProps={{ style: { maxHeight: 200, width: isMobile ? "90vw" : 200 } }}
      >
        <MenuItem onClick={() => startEdit(selectedRow)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* MODAL */}
      <SalesMan
        open={openModal}
        onClose={closeModal}
        formData={formData}
        setFormData={setFormData}
        onSubmit={afterSubmit}
      />

      {/* DELETE CONFIRM DIALOG */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ mr: 2, mb: 2 }}>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setSelectedRow(null);
              setAnchorEl(null);
            }}
            sx={{ border: `1px solid ${Color.secondary.main}`, color: Color.label.main }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesManList;